"use client";

import React from "react";
import { CircleCheck as CheckCircle2, ArrowRight, House as Home, Wrench, Zap } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function WholeHomeRenovationHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  const includedScope = [{
    icon: Home,
    title: "Coordinated multi-room scope",
    description: "Comprehensive overhaul of primary suites, guest bedrooms, kitchens, baths, and living areas managed under one unified design timeline."
  }, {
    icon: Wrench,
    title: "Structural & layout transformations",
    description: "Load-bearing wall removals, open-concept conversions, doorway repositioning, ceiling elevation, and floor plan reconfigurations."
  }, {
    icon: Zap,
    title: "Full mechanical, electrical & plumbing updates",
    description: "Modernized high-efficiency HVAC ducting and zoning, complete panel upgrades with recessed architectural lighting, and fresh plumbing supply lines."
  }];
  return <section id="wholehomerenovationhero" data-nav="light" ref={ref} className="relative overflow-hidden bg-background py-16 text-foreground md:py-24 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Content Column */}
          <div className={`transition-all duration-700 ease-out lg:col-span-7 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3.5 py-1 text-xs font-semibold tracking-wide text-foreground uppercase shadow-2xs">
              <span className="size-2 rounded-full bg-primary" />
              Brookhaven Residential Services
            </div>

            <h1 className="mt-4 font-serif text-4xl leading-tight font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Whole-home renovations in Brookhaven
            </h1>

            {/* Direct 2-3 sentence answer */}
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground sm:text-xl">
              A whole-home renovation in Brookhaven is a full-scale transformation of your existing residence that reimagines both aesthetics and core infrastructure simultaneously. Rather than tackling isolated rooms over several years, this process synchronizes structural engineering, modern interior architecture, and upgraded utility systems into one seamless build phase. The outcome is a cohesive, high-performance home tailored specifically to your family's routine without requiring you to leave the neighborhood you love.
            </p>

            <Separator className="my-8" />

            {/* What's Included Section */}
            <div>
              <h2 className="text-sm font-semibold tracking-wider text-secondary uppercase">
                What's Included
              </h2>

              <ul className="mt-5 space-y-4">
                {includedScope.map((item, index) => {
                const Icon = item.icon;
                return <li key={item.title} data-index={index} className="flex items-start gap-3.5 rounded-lg border border-border/60 bg-card p-4 shadow-2xs transition-all hover:border-primary/40 hover:shadow-xs">
                      <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-accent-foreground">
                        <Icon className="size-4 text-primary" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="text-base font-semibold text-foreground">
                          {item.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {item.description}
                        </p>
                      </div>
                    </li>;
              })}
              </ul>
            </div>

            {/* Action CTA */}
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <Button asChild size="lg" className="h-12 px-7 text-base font-medium">
                <Link to="/ContactRequestAnEstimatePage">
                  Request an estimate
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="h-12 border-border px-7 text-base font-medium text-foreground hover:bg-muted">
                <Link to="/OurProjectsPage">View recent projects</Link>
              </Button>
            </div>
          </div>

          {/* Media Column */}
          <div className={`transition-all delay-200 duration-700 ease-out lg:col-span-5 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
            <div className="relative mx-auto max-w-lg lg:max-w-none">
              <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation_interior.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation_interior.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_whole_home_renovation_interior.png-wvc-sizes" alt="Open-concept whole-home renovation in Brookhaven showcasing remodeled living room and integrated custom kitchen" loading="lazy" decoding="async" className="h-[420px] w-full object-cover sm:h-[480px]" />
              </div>

              {/* Informative Floating Card */}
              <Card className="absolute -bottom-6 -left-4 max-w-xs border border-border/80 bg-card p-4 shadow-lg sm:-bottom-8 sm:-left-6">
                <CardContent className="p-0">
                  <div className="flex items-center gap-2.5">
                    <CheckCircle2 className="size-5 shrink-0 text-primary" />
                    <p className="text-xs font-semibold text-foreground">
                      Single-source design, permitting & construction management
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}